/**
 * Service-Paket-Gültigkeitsfenster vs. Abwesenheitsdauer.
 *
 * Jedes Paket deckt eine maximale Abwesenheitsdauer ab:
 *   - Service-Paket 1: unbegrenzt (fortlaufend bis auf Widerruf) → nie Warnung
 *   - Service-Paket 2: bis zu 3 Monate
 *   - Service-Paket 3: bis zu 2 Wochen (14 Tage)
 *
 * Die Warnung ist reihenfolgen-unabhängig: dieselbe Prüfung wird sowohl beim
 * Planen einer Abwesenheit (Paket bereits gebucht) als auch beim Wählen eines
 * Pakets (Abwesenheit bereits geplant) verwendet.
 */

// true, wenn die Abwesenheit LÄNGER ist als das vom Paket abgedeckte Fenster.
export function absenceExceedsPackageWindow(packageId, fromDate, toDate) {
  if (!packageId || !fromDate || !toDate) return false;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;

  if (packageId === "3") {
    const nights = Math.max(0, Math.round((to - from) / 86400000));
    return nights > 14;
  }
  if (packageId === "2") {
    // Gültig bis zu 3 Monate ab Abreisedatum.
    const limit = new Date(from);
    limit.setMonth(limit.getMonth() + 3);
    return to > limit;
  }
  // Paket 1 (und Unbekannte): keine Begrenzung.
  return false;
}

// Kürzestes Paket, das die gesamte Abwesenheitsdauer noch abdeckt (nicht
// blockiert wird). Reihenfolge: 3 (14 Tage) → 2 (3 Monate) → 1 (unbegrenzt).
// Leitet sich zentral aus `absenceExceedsPackageWindow` ab (keine doppelten
// Grenzwerte im Frontend). Rückgabe: Paket-ID als String oder null.
export function recommendedPackageForAbsence(fromDate, toDate) {
  if (!fromDate || !toDate) return null;
  for (const id of ["3", "2", "1"]) {
    if (!absenceExceedsPackageWindow(id, fromDate, toDate)) return id;
  }
  return "1";
}

export function packageWarningMessage(packageId, de = true, fromDate = null, toDate = null) {
  const windows = de
    ? { "3": "nur 14 Tage (zwei Wochen)", "2": "nur 3 Monate" }
    : { "3": "only 14 days (two weeks)", "2": "only 3 months" };
  const win = windows[packageId] || (de ? "einen kürzeren Zeitraum" : "a shorter period");
  let dur = de ? "die geplante Dauer" : "the planned duration";
  const f = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
  const t = toDate ? new Date(`${toDate}T00:00:00`) : null;
  if (f && t && !Number.isNaN(f.getTime()) && !Number.isNaN(t.getTime())) {
    const days = Math.max(0, Math.round((t - f) / 86400000));
    const fmt = (d) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    dur = de ? `${days} Tage (${fmt(f)} – ${fmt(t)})` : `${days} days (${fmt(f)} – ${fmt(t)})`;
  }
  return de
    ? `Diese Abwesenheit dauert ${dur}, Service-Paket ${packageId} deckt jedoch ${win} ab. Buchung nicht möglich – bitte wählen Sie ein passendes Paket (Service-Paket 2 oder Service-Paket 1) oder passen Sie die Abwesenheitsdauer an.`
    : `This absence lasts ${dur}, but Service Package ${packageId} covers ${win}. Booking not possible – please choose a suitable package (Service Package 2 or Service Package 1) or adjust the absence duration.`;
}

// Enddatum aus der Paketdauer (nur Anzeige/Vorschau; Backend ist maßgeblich).
// Paket 3 = Start +14 Tage, Paket 2 = Start +3 Monate (Monatsende geklammert),
// Paket 1 = fortlaufend → null. startDate = ISO 'YYYY-MM-DD'.
export function packageEndDate(packageId, startDate) {
  if (!packageId || !startDate) return null;
  const start = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (packageId === "3") {
    const d = new Date(start);
    d.setDate(d.getDate() + 14);
    return iso(d);
  }
  if (packageId === "2") {
    const dom = start.getDate();
    const d = new Date(start);
    d.setMonth(d.getMonth() + 3);
    if (d.getDate() !== dom) d.setDate(0); // Monatsüberlauf → letzter Tag des Zielmonats
    return iso(d);
  }
  return null; // Paket 1 fortlaufend
}

// --- Modell A · Service-Paket-KETTE (Zeitfenster pro Objekt) ------------------
// Spiegelt exakt die Backend-Logik in routers/service_packages.py.

function _plusDaysIso(iso, n) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Paketbuchungen → Fenster [{start, end|null}] eines Objekts (start_date Pflicht,
// end=null ⟺ Paket 1 offen). Buchungen ohne start_date zählen nicht.
export function windowsFromBookings(bookings, propertyId) {
  return (bookings || [])
    .filter((b) => b && b.start_date && (!propertyId || b.property_id === propertyId))
    .map((b) => ({ start: b.start_date, end: b.end_date || null }));
}

// Aneinandergrenzende/überlappende Fenster zu maximalen Intervallen mergen.
function _mergeWindows(windows) {
  const ws = (windows || []).filter((w) => w.start).slice().sort((a, b) => a.start.localeCompare(b.start));
  const merged = [];
  for (const w of ws) {
    if (merged.length) {
      const prev = merged[merged.length - 1];
      if (prev.end === null) continue; // offenes Vorgängerfenster deckt alles ab prev.start
      if (w.start <= _plusDaysIso(prev.end, 1)) {
        prev.end = w.end === null ? null : (w.end > prev.end ? w.end : prev.end);
        continue;
      }
    }
    merged.push({ start: w.start, end: w.end });
  }
  return merged;
}

// True, wenn [from,to] VOLLSTÄNDIG in EIN gemergtes Fenster fällt (Union der
// lückenlosen Kette). Teilabdeckung reicht nicht.
export function absenceCoveredByChain(bookings, propertyId, fromDate, toDate) {
  if (!propertyId || !fromDate || !toDate) return false;
  return _mergeWindows(windowsFromBookings(bookings, propertyId))
    .some((w) => w.start <= fromDate && (w.end === null || toDate <= w.end));
}

// Anschluss-Status der Kette: {mode:'open'|'follow'|'first', start:'YYYY-MM-DD'|null}
//  open   = Paket 1 (offenes Ende) aktiv → keine weitere Buchung nötig
//  follow = aktuelles/zukünftiges endliches Fenster → Start = letztes Ende + 1
//  first  = kein aktives Fenster → Kunde wählt Start
export function chainStart(bookings, propertyId, todayIso, absenceFrom = null) {
  const merged = _mergeWindows(windowsFromBookings(bookings, propertyId));
  const ref = absenceFrom || todayIso;
  // Offenes Paket 1, das den (Abwesenheits-)Beginn abdeckt → keine Buchung nötig.
  if (merged.some((w) => w.end === null && w.start <= ref)) return { mode: "open", start: null };
  // Ohne bekannten Beginn: an letztes Zukunftsfenster anschließen (Alt-Verhalten).
  if (!absenceFrom) {
    const fe = merged.filter((w) => w.end && w.end >= todayIso).map((w) => w.end).sort();
    if (fe.length) return { mode: "follow", start: _plusDaysIso(fe[fe.length - 1], 1) };
    return { mode: "first", start: null };
  }
  // Nahtloser Anschluss NUR an ein VERBINDENDES endliches Fenster: es enthält den
  // Abwesenheitsbeginn oder endet unmittelbar davor (start ≤ from ≤ Ende + 1).
  // Sonst (echte Lücke oder Abwesenheit liegt VOR allen Fenstern) startet das
  // neue Paket zum Abwesenheitsbeginn — der Kunde wählt frei.
  const connecting = merged
    .filter((w) => w.end !== null && w.start <= absenceFrom && absenceFrom <= _plusDaysIso(w.end, 1))
    .map((w) => w.end)
    .sort();
  if (connecting.length) return { mode: "follow", start: _plusDaysIso(connecting[connecting.length - 1], 1) };
  return { mode: "first", start: null };
}

// Bereits gebuchte Fenster, die NACH `startIso` beginnen und sich mit dem neuen
// Paket [startIso, endIso|null] überschneiden (endIso=null ⟺ Paket 1 offen →
// überlappt alle späteren Fenster). Dient dem Überschneidungs-Hinweis.
export function laterOverlappingWindows(bookings, propertyId, startIso, endIso) {
  if (!propertyId || !startIso) return [];
  return windowsFromBookings(bookings, propertyId)
    .filter((w) => w.start > startIso && (endIso === null || w.start <= endIso))
    .sort((a, b) => a.start.localeCompare(b.start));
}

// Kürzestes Paket, das AB `startIso` bis `toDate` reicht (Paket 1 immer genug).
export function recommendedPackageFromStart(startIso, toDate) {
  if (!startIso || !toDate) return null;
  for (const id of ["3", "2", "1"]) {
    const end = packageEndDate(id, startIso);
    if (end === null || toDate <= end) return id;
  }
  return "1";
}

// Vorlauf-Fenster für den Verlängerungs-Hinweis (Tage vor Paketende).
// Paket 2 = 14 Tage, Paket 3 = 3 Tage, Paket 1 = nie (fortlaufend, kein Ende).
export const RENEWAL_LEAD_DAYS = { "2": 14, "3": 3 };

// Fällige Verlängerung eines Objekts: das endliche Kettenende (Paket 2/3), das
// innerhalb seines Vorlauffensters ausläuft und noch NICHT verlängert wurde
// (offenes Paket 1 oder Folgepaket → keine Warnung). Spiegelt das Backend.
// Rückgabe: {packageId, packageTitle, endDate, daysLeft, seamlessStart} | null.
export function packageRenewalDue(bookings, propertyId, todayIso) {
  if (!propertyId || !todayIso) return null;
  const wins = windowsFromBookings(bookings, propertyId);
  if (wins.some((w) => w.end === null)) return null; // Paket 1 offen → kein Ablauf
  const futureEnds = wins.filter((w) => w.end && w.end >= todayIso).map((w) => w.end).sort();
  if (!futureEnds.length) return null;
  const tailEnd = futureEnds[futureEnds.length - 1];
  const booking = (bookings || []).find(
    (b) => b && b.property_id === propertyId && (b.end_date || null) === tailEnd
  );
  const pid = booking?.package_id;
  const lead = RENEWAL_LEAD_DAYS[pid];
  if (!lead) return null;
  const daysLeft = Math.round(
    (new Date(`${tailEnd}T00:00:00`) - new Date(`${todayIso}T00:00:00`)) / 86400000
  );
  if (daysLeft < 0 || daysLeft > lead) return null;
  return {
    packageId: pid,
    packageTitle: booking?.package_title || `Service-Paket ${pid}`,
    endDate: tailEnd,
    daysLeft,
    seamlessStart: _plusDaysIso(tailEnd, 1),
  };
}

// Coverage-Meldung (spiegelt Backend coverage_warning_message).
export function coverageWarningMessage(fromDate, toDate, de = true) {
  let dur = de ? "den geplanten Zeitraum" : "the planned period";
  const f = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
  const t = toDate ? new Date(`${toDate}T00:00:00`) : null;
  if (f && t && !Number.isNaN(f.getTime()) && !Number.isNaN(t.getTime())) {
    const days = Math.max(0, Math.round((t - f) / 86400000));
    const fmt = (d) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    dur = de ? `${days} Tage (${fmt(f)} – ${fmt(t)})` : `${days} days (${fmt(f)} – ${fmt(t)})`;
  }
  return de
    ? `Für diese Abwesenheit (${dur}) besteht noch kein durchgehendes Service-Paket. Bitte buchen oder verlängern Sie ein passendes Paket, das den gesamten Zeitraum abdeckt.`
    : `This absence (${dur}) is not yet covered by a continuous service package. Please book or extend a suitable package that covers the entire period.`;
}
